import 'reflect-metadata';
import {CacheKey, Deserializer, JsonProperty, ObjectMapper, Serializer} from "json-object-mapper";
import {ArrayFieldDataConfigSerializerDeserializer, FieldDataConfig} from "./fields.model";

export class Channel {
  @JsonProperty({name: "owner"})
  public owner?: string = undefined;

  @JsonProperty({name: "vertical"})
  public vertical?: string = undefined;

  @JsonProperty({name: "code"})
  public code?: string = undefined;

}

export class Batch {
  @JsonProperty({name: "id"})
  public id?: string = undefined;

  @JsonProperty({type: Channel, name: "channel"})
  public channel?: Channel = undefined;
}

export class ChannelConfig {
  @JsonProperty({name: "owner"})
  public owner?: string = undefined;

  @JsonProperty({name: "vertical"})
  public vertical?: string = undefined;

  @JsonProperty({name: "code"})
  public code?: string = undefined;

  @JsonProperty({name: "frontend"})
  public frontend?: string = undefined;

  @JsonProperty({name: "data"})
  public data?: string = undefined;

  @JsonProperty({name: "process"})
  public process?: string = undefined;

  @JsonProperty({name: "description"})
  public description?: string = undefined;
}

export class InputConfig {
  @JsonProperty({name: "source"})
  public source?: string = undefined;

  @JsonProperty({name: "transformer"})
  public transformer?: string = undefined;

  @JsonProperty({type: FieldDataConfig, deserializer: ArrayFieldDataConfigSerializerDeserializer, name: "fields"})
  public fields?: FieldDataConfig[] = undefined;

  @JsonProperty({name: "mapping"})
  public mapping?: Record<string, Record<string, string>> = undefined;

  @JsonProperty({name: "settings"})
  public settings?: Record<string, string> = undefined;
}

export class CheckConfig {
  @JsonProperty({name: "list_owner"})
  public listOwner?: string = undefined;

  @JsonProperty({name: "list_vertical"})
  public listVertical?: string = undefined;

  @JsonProperty({name: "list_code"})
  public listCode?: string = undefined;

  @JsonProperty({name: "list_field"})
  public listField?: string = undefined;

  @JsonProperty({name: "data_owner"})
  public dataOwner?: string = undefined;

  @JsonProperty({name: "data_vertical"})
  public dataVertical?: string = undefined;

  @JsonProperty({name: "data_code"})
  public dataCode?: string = undefined;

  @JsonProperty({name: "data_field"})
  public dataField?: string = undefined;

  @JsonProperty({name: "definition"})
  public definition?: string = undefined;

  @JsonProperty({name: "threshold"})
  public threshold?: number = undefined;

  @JsonProperty({name: "value"})
  public value?: number = undefined;

  @JsonProperty({name: "hint"})
  public hint?: string = undefined;

  @JsonProperty({name: "reason"})
  public reason?: string = undefined;

}

export class RiskConfig {
  @JsonProperty({name: "minimum"})
  public minimum?: number = undefined;

  @JsonProperty({name: "maximum"})
  public maximum?: number = undefined;

  @JsonProperty({name: "label"})
  public label?: string = undefined;

}

@CacheKey("ArrayCheckConfigSerializerDeserializer")
class ArrayCheckConfigSerializerDeserializer implements Deserializer, Serializer {
  deserialize = (values: Object[]): CheckConfig[] => {
    return values.map(v => {
      return ObjectMapper.deserialize(CheckConfig, v);
    })
  }
  serialize = (values: CheckConfig[]): Object[] => {
    return values.map(v => {
      return ObjectMapper.serialize(v);
    })
  }
}

@CacheKey("ArrayRiskConfigSerializerDeserializer")
class ArrayRiskConfigSerializerDeserializer implements Deserializer, Serializer {
  deserialize = (values: Object[]): RiskConfig[] => {
    return values.map(v => {
      return ObjectMapper.deserialize(RiskConfig, v);
    })
  }
  serialize = (values: RiskConfig[]): Object[] => {
    return values.map(v => {
      return ObjectMapper.serialize(v);
    })
  }
}

export class ChecksSettings {
  @JsonProperty({name: "aggregator"})
  public aggregator?: string = undefined;

  @JsonProperty({type: CheckConfig, deserializer: ArrayCheckConfigSerializerDeserializer, name: "checks"})
  public checks?: CheckConfig[] = [];

  @JsonProperty({type: RiskConfig, deserializer: ArrayRiskConfigSerializerDeserializer, name: "risks"})
  public risks?: RiskConfig[] = [];

  @JsonProperty({name: "settings"})
  public settings?: Record<string, string> = undefined;
}

export class LabelConfig {
  @JsonProperty({name: "value"})
  public value?: string = undefined;

  @JsonProperty({name: "condition"})
  public condition?: string = undefined;
}

@CacheKey("ArrayLabelConfigSerializerDeserializer")
class ArrayLabelConfigSerializerDeserializer implements Deserializer, Serializer {
  deserialize = (values: Object[]): LabelConfig[] => {
    return values.map(v => {
      return ObjectMapper.deserialize(LabelConfig, v);
    })
  }
  serialize = (values: LabelConfig[]): Object[] => {
    return values.map(v => {
      return ObjectMapper.serialize(v);
    })
  }
}

export class VariableConfig {
  @JsonProperty({name: "name"})
  public name?: string = undefined;

  @JsonProperty({name: "type"})
  public kind?: string = undefined;

  @JsonProperty({name: "value"})
  public value?: string = undefined;
}

@CacheKey("ArrayVariableConfigSerializerDeserializer")
class ArrayVariableConfigSerializerDeserializer implements Deserializer, Serializer {
  deserialize = (values: Object[]): VariableConfig[] => {
    return values.map(v => {
      return ObjectMapper.deserialize(VariableConfig, v);
    })
  }
  serialize = (values: VariableConfig[]): Object[] => {
    return values.map(v => {
      return ObjectMapper.serialize(v);
    })
  }
}

export class ProfileAggregateConfig {
  @JsonProperty({name: "name"})
  public name?: string = undefined;

  @JsonProperty({name: "operation"})
  public operation?: string = undefined;

  @JsonProperty({name: "values"})
  public values?: string[] = [];

  @JsonProperty({name: "key"})
  public key?: string = undefined;

  @JsonProperty({name: "groupby"})
  public groupby?: string = undefined;
}

@CacheKey("ArrayProfileAggregateConfigSerializerDeserializer")
class ArrayProfileAggregateConfigSerializerDeserializer implements Deserializer, Serializer {
  deserialize = (values: Object[]): ProfileAggregateConfig[] => {
    return values.map(v => {
      return ObjectMapper.deserialize(ProfileAggregateConfig, v);
    })
  }
  serialize = (values: ProfileAggregateConfig[]): Object[] => {
    return values.map(v => {
      return ObjectMapper.serialize(v);
    })
  }
}

export class ProfileStateConfig {
  @JsonProperty({name: "name"})
  public name?: string = undefined;

  @JsonProperty({name: "labels"})
  public labels?: string[] = [];

  @JsonProperty({name: "period"})
  public period?: string = undefined;

  @JsonProperty({name: "duration"})
  public duration?: number = undefined;

  @JsonProperty({name: "aggregates", type: ProfileAggregateConfig, deserializer: ArrayProfileAggregateConfigSerializerDeserializer})
  public aggregates: ProfileAggregateConfig[] = [];
}

@CacheKey("ArrayProfileStateConfigSerializerDeserializer")
class ArrayProfileStateConfigSerializerDeserializer implements Deserializer, Serializer {
  deserialize = (values: Object[]): ProfileStateConfig[] => {
    return values.map(v => {
      return ObjectMapper.deserialize(ProfileStateConfig, v);
    })
  }
  serialize = (values: ProfileStateConfig[]): Object[] => {
    return values.map(v => {
      return ObjectMapper.serialize(v);
    })
  }
}

export class ProfileConfig {
  @JsonProperty({name: "name"})
  public name?: string = undefined;

  @JsonProperty({name: "channel"})
  public channel?: Channel = undefined;

  @JsonProperty({name: "keys"})
  public keys?: string[] = [];

  @JsonProperty({name: "granularity"})
  public granularity?: string = undefined;

  @JsonProperty({name: "reset"})
  public reset?: boolean = undefined;

  @JsonProperty({name: "values", type: VariableConfig, deserializer: ArrayVariableConfigSerializerDeserializer})
  public values: VariableConfig[] = [];

  @JsonProperty({name: "states"})
  public states?: ProfileStateConfig[] = [];
}

@CacheKey("ArrayProfileStateConfigSerializerDeserializer")
class ArrayProfileConfigSerializerDeserializer implements Deserializer, Serializer {
  deserialize = (values: Object[]): ProfileConfig[] => {
    return values.map(v => {
      return ObjectMapper.deserialize(ProfileConfig, v);
    })
  }
  serialize = (values: ProfileConfig[]): Object[] => {
    return values.map(v => {
      return ObjectMapper.serialize(v);
    })
  }
}

export class RuleConfig {
  @JsonProperty({name: "code"})
  public code?: string = undefined;

  @JsonProperty({name: "value"})
  public value?: number = undefined;

  @JsonProperty({name: "score"})
  public score?: string = undefined;

  @JsonProperty({name: "description"})
  public description?: string = undefined;

  @JsonProperty({name: "positive"})
  public positive?: string = undefined;

  @JsonProperty({name: "negative"})
  public negative?: string = undefined;

  @JsonProperty({name: "labels"})
  public labels?: string[] = [];

  @JsonProperty({name: "condition"})
  public condition?: string = undefined;

  @JsonProperty({type: VariableConfig, deserializer: ArrayVariableConfigSerializerDeserializer, name: "variables"})
  public variables?: VariableConfig[] = undefined;

  @JsonProperty({name: "action"})
  public action?: string = undefined;

  @JsonProperty({name: "reason"})
  public reason?: string = undefined;
}

@CacheKey("ArrayRuleConfigSerializerDeserializer")
class ArrayRuleConfigSerializerDeserializer implements Deserializer, Serializer {
  deserialize = (values: Object[]): RuleConfig[] => {
    return values.map(v => {
      return ObjectMapper.deserialize(RuleConfig, v);
    })
  }
  serialize = (values: RuleConfig[]): Object[] => {
    return values.map(v => {
      return ObjectMapper.serialize(v);
    })
  }
}

export class RulesSettings {
  @JsonProperty({name: "code"})
  public code?: string = undefined;

  @JsonProperty({name: "name"})
  public name?: string = undefined;

  @JsonProperty({name: "description"})
  public description?: string = undefined;

  @JsonProperty({type: VariableConfig, deserializer: ArrayVariableConfigSerializerDeserializer, name: "variables"})
  public variables?: VariableConfig[] = [];

  @JsonProperty({type: LabelConfig, deserializer: ArrayLabelConfigSerializerDeserializer, name: "labels"})
  public labels?: LabelConfig[] = [];

  @JsonProperty({type: ProfileConfig, deserializer: ArrayProfileConfigSerializerDeserializer, name: "profiles"})
  public profiles?: ProfileConfig[] = [];

  @JsonProperty({type: RuleConfig, deserializer: ArrayRuleConfigSerializerDeserializer, name: "references"})
  public references?: RuleConfig[] = [];

  @JsonProperty({type: RuleConfig, deserializer: ArrayRuleConfigSerializerDeserializer, name: "services"})
  public services?: RuleConfig[] = [];

  @JsonProperty({type: RuleConfig, deserializer: ArrayRuleConfigSerializerDeserializer, name: "rules"})
  public rules?: RuleConfig[] = [];
}

export class FeatureClass {
  @JsonProperty({name: "name"})
  public name?: string = undefined;

  @JsonProperty({name: "length"})
  public length?: number = undefined;
}

@CacheKey("ArrayFeatureClassSerializerDeserializer")
class ArrayFeatureClassSerializerDeserializer implements Deserializer, Serializer {
  deserialize = (values: Object[]): FeatureClass[] => {
    return values.map(v => {
      return ObjectMapper.deserialize(FeatureClass, v);
    })
  }
  serialize = (values: FeatureClass[]): Object[] => {
    return values.map(v => {
      return ObjectMapper.serialize(v);
    })
  }
}

export class DecisionConfig {
  @JsonProperty({name: "channel", type: Channel})
  public channel?: Channel = undefined;

  @JsonProperty({name: "model"})
  public model?: string = undefined;

  @JsonProperty({name: "settings"})
  public settings?: Record<string, string> = undefined;

  @JsonProperty({type: FeatureClass, deserializer: ArrayFeatureClassSerializerDeserializer, name: "feature-classes"})
  public features?: FeatureClass[] = [];
}

export class ChannelDefinition {
  @JsonProperty({type: ChannelConfig, name: "config"})
  public config?: ChannelConfig = undefined;

  @JsonProperty({type: InputConfig, name: "input"})
  public input?: InputConfig = undefined;

  @JsonProperty({type: ChecksSettings, name: "checks"})
  public checks?: ChecksSettings = undefined;

  @JsonProperty({type: RulesSettings, name: "rules"})
  public rules?: RulesSettings = undefined;

  @JsonProperty({type: DecisionConfig, name: "decision"})
  public decision: DecisionConfig = new DecisionConfig();
}
